import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  paginateLocally,
  queryKeys,
  type PaginatedResult,
} from "@nasam/kernel";
import type {
  CatalogRepository,
  SellableModule,
  SellableModulesListQuery,
  SellingPriceUpdateInput,
} from "../domain";

/**
 * The endpoint takes no query params at all — the whole holding is fetched
 * once and cached; every filter/sort/page below is applied client-side over
 * that one response.
 */
export function useSellableModulesQuery(repository: CatalogRepository) {
  return useQuery({
    queryKey: queryKeys.modules.list(),
    queryFn: () => repository.list(),
  });
}

function matches(value: string, query: string | undefined): boolean {
  return (
    query === undefined ||
    value.toLocaleLowerCase().includes(query.toLocaleLowerCase())
  );
}

export function paginateSellableModules(
  modules: readonly SellableModule[],
  query: SellableModulesListQuery,
): PaginatedResult<SellableModule> {
  const sortBy = query.sortBy ?? "nameEn";
  const direction = query.sort === "desc" ? -1 : 1;

  const filtered = modules
    .filter(
      (module) =>
        matches(module.nameEn, query.search) ||
        matches(module.nameAr, query.search),
    )
    .toSorted((left, right) => {
      const leftValue = left[sortBy];
      const rightValue = right[sortBy];
      const result =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue ?? "").localeCompare(String(rightValue ?? ""));
      return result * direction;
    });

  return paginateLocally(filtered, query.page ?? 1, query.limit ?? 10);
}

export function useUpdateSellingPriceMutation(repository: CatalogRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: SellingPriceUpdateInput;
    }) => repository.updateSellingPrice(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.modules.all }),
  });
}
